import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MapPin, Check, ShieldCheck, Compass, ArrowRight, X } from "lucide-react";
import RALayout from "../components/ra/RALayout";
import { getPublicCampaign, createLead, createBooking, payBooking, mediaUrl } from "../lib/adventureApi";
import { storeAttribution, getAttribution } from "../lib/attribution";

const field = "h-11 w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 focus:outline-none focus:ring-2 focus:ring-terracotta";
const lbl = "font-mono text-[11px] uppercase tracking-widest text-ink-secondary mb-1.5 block";
const fmt = (n) => (n ? `A$${Number(n).toLocaleString()}` : "");

function LeadForm({ campaign }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", country: "", travel_dates: "", travellers: 2, service_level: "", message: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async () => {
    if (!f.name || !f.email) { toast.error("Please add your name and email"); return; }
    setBusy(true);
    const attr = getAttribution() || {};
    try {
      const r = await createLead({
        ...f, travellers: Number(f.travellers) || 1,
        campaign_code: attr.campaign || campaign?.code,
        source: attr.source, source_channel: attr.channel, landing_page: attr.landing || window.location.pathname,
        destination: campaign?.destination,
      });
      setDone(r.ref);
    } catch { toast.error("Something went wrong — please try again"); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="text-center py-6" data-testid="lead-success">
        <div className="w-14 h-14 rounded-full bg-moss-light flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-moss" /></div>
        <h3 className="font-display font-extrabold text-xl mt-3">Thank you!</h3>
        <p className="text-ink-secondary mt-2 text-sm">Your enquiry <b>{done}</b> is with our Responsible Adventures team — we'll be in touch personally within one business day.</p>
        <Link to="/build" data-testid="lead-build-cta" className="mt-5 inline-flex items-center gap-2 bg-ink text-white h-11 px-5 rounded-sm font-semibold text-sm">Start building your itinerary <ArrowRight className="w-4 h-4" /></Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className={lbl}>Name *</label><input data-testid="lead-name" className={field} value={f.name} onChange={set("name")} /></div>
        <div><label className={lbl}>Email *</label><input data-testid="lead-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="you@example.com" /></div>
        <div><label className={lbl}>Mobile</label><input data-testid="lead-phone" className={field} value={f.phone} onChange={set("phone")} /></div>
        <div><label className={lbl}>Country</label><input data-testid="lead-country" className={field} value={f.country} onChange={set("country")} /></div>
        <div><label className={lbl}>Preferred travel dates</label><input data-testid="lead-dates" className={field} value={f.travel_dates} onChange={set("travel_dates")} placeholder="e.g. October 2026" /></div>
        <div><label className={lbl}>Travellers</label><input data-testid="lead-travellers" type="number" min="1" className={field} value={f.travellers} onChange={set("travellers")} /></div>
      </div>
      <div><label className={lbl}>Preferred service level</label>
        <select data-testid="lead-service" className={field} value={f.service_level} onChange={set("service_level")}>
          <option value="">No preference</option><option>Comfort</option><option>Premium</option><option>Luxury</option>
        </select>
      </div>
      <div><label className={lbl}>Message</label><textarea data-testid="lead-message" className="w-full rounded-sm bg-white border border-sand-dark text-ink text-sm px-3 py-2 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-terracotta" value={f.message} onChange={set("message")} /></div>
      <label className="flex items-start gap-2.5 text-xs text-ink-secondary"><input type="checkbox" data-testid="lead-consent" checked={f.marketing_consent} onChange={set("marketing_consent")} className="mt-0.5 accent-terracotta" /> I agree to be contacted by Responsible Adventures about my trip and accept the privacy policy.</label>
      <button onClick={submit} disabled={busy} data-testid="lead-submit" className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Enquire now"}
      </button>
    </div>
  );
}

function ReserveForm({ campaign }) {
  const [f, setF] = useState({ name: "", email: "", travellers: 2 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const total = (Number(campaign?.from_price) || 0) * (Number(f.travellers) || 1);

  const reserve = async () => {
    if (!f.name || !f.email) { toast.error("Please add your name and email"); return; }
    setBusy(true);
    const attr = getAttribution() || {};
    try {
      const b = await createBooking({
        campaign_code: attr.campaign || campaign?.code, source: attr.source,
        trip_name: campaign?.name, tour_slug: campaign?.tour_slug,
        travellers: Number(f.travellers) || 1, name: f.name, email: f.email,
      });
      const p = await payBooking(b.ref, { payment_type: "deposit", origin_url: window.location.origin });
      if (p.checkout_url) { window.location.href = p.checkout_url; return; }
      toast.error("Couldn't start payment");
      setBusy(false);
    } catch { toast.error("Something went wrong — please try again"); setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="reserve-form">
      <div><label className={lbl}>Name *</label><input data-testid="reserve-name" className={field} value={f.name} onChange={set("name")} /></div>
      <div><label className={lbl}>Email *</label><input data-testid="reserve-email" type="email" className={field} value={f.email} onChange={set("email")} placeholder="you@example.com" /></div>
      <div><label className={lbl}>Travellers</label><input data-testid="reserve-travellers" type="number" min="1" className={field} value={f.travellers} onChange={set("travellers")} /></div>
      <div className="rounded-sm bg-sand p-3 text-sm">
        <div className="flex justify-between text-ink-secondary"><span>Trip total</span><span className="tabular-nums">{fmt(total)}</span></div>
        <div className="flex justify-between font-semibold text-ink mt-1"><span>Deposit due now</span><span className="tabular-nums">A$500</span></div>
      </div>
      <button onClick={reserve} disabled={busy} data-testid="reserve-pay-btn" className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold tracking-widest uppercase text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting secure checkout…</> : "Reserve & pay A$500 deposit"}
      </button>
      <p className="text-[11px] text-ink-muted text-center">Secure Stripe checkout · pay the balance later from your booking link.</p>
    </div>
  );
}

export default function AdventureLanding() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [state, setState] = useState("loading"); // loading | live | unavailable | notfound
  const [campaign, setCampaign] = useState(null);
  const [mode, setMode] = useState("enquire");

  useEffect(() => {
    const campaignCode = params.get("campaign");
    const source = params.get("source");
    const channel = params.get("channel");
    storeAttribution({ campaign: campaignCode, source, channel, landing: window.location.pathname + window.location.search });

    const attr = getAttribution();
    const code = campaignCode || attr?.campaign;
    if (!code) { setState("notfound"); return; }
    getPublicCampaign(code).then((d) => {
      if (!d.available) { setCampaign(d.campaign); setState("unavailable"); return; }
      setCampaign(d.campaign); setState("live");
    }).catch(() => setState("notfound"));
  }, [slug, params]);

  const hero = campaign?.hero_image_url ? mediaUrl(campaign.hero_image_url) : null;

  return (
    <RALayout>
      <div data-testid="adventure-landing">
        {state === "loading" && <div className="py-32 text-center text-ink-secondary"><Loader2 className="w-6 h-6 animate-spin text-terracotta inline" /></div>}

        {state === "notfound" && (
          <div className="py-28 text-center max-w-md mx-auto px-6" data-testid="landing-notfound">
            <Compass className="w-10 h-10 text-terracotta mx-auto" />
            <h1 className="font-display font-extrabold text-2xl mt-4">Offer not found</h1>
            <p className="text-ink-secondary mt-2">This campaign link may be incorrect. Explore our adventures instead.</p>
            <Link to="/tours" className="mt-5 inline-block bg-ink text-white h-11 px-6 rounded-sm font-semibold leading-[44px]">Browse tours</Link>
          </div>
        )}

        {state === "unavailable" && (
          <div className="py-28 text-center max-w-md mx-auto px-6" data-testid="landing-unavailable">
            <h1 className="font-display font-extrabold text-2xl">This offer is no longer available</h1>
            <p className="text-ink-secondary mt-2">But the adventure lives on — see our current {campaign?.destination || ""} trips.</p>
            <Link to="/tours" className="mt-5 inline-block bg-terracotta text-white h-11 px-6 rounded-sm font-semibold leading-[44px]">See current offers</Link>
          </div>
        )}

        {state === "live" && campaign && (
          <>
            <section className="relative overflow-hidden">
              {hero ? <img src={hero} alt={campaign.name} className="absolute inset-0 w-full h-full object-cover" />
                : <div className="absolute inset-0 bg-gradient-to-br from-[#221c17] via-[#33261d] to-[#4a2c20]" />}
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/55 to-ink/25" />
              <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
                {campaign.destination && <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {campaign.destination}</p>}
                <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02] mt-3 max-w-3xl" data-testid="landing-title">{campaign.name}</h1>
                {campaign.caption && <p className="text-white/80 text-lg mt-4 max-w-xl">{campaign.caption}</p>}
                {campaign.from_price ? <p className="text-white mt-6 font-display font-bold text-2xl" data-testid="landing-price">From {fmt(campaign.from_price)} <span className="text-white/60 text-base font-normal">per person</span></p> : null}
              </div>
            </section>

            <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[1.2fr,1fr] gap-10">
              <div className="space-y-8">
                {campaign.inclusions && (
                  <div><h2 className="font-display font-extrabold text-xl mb-3">What's included</h2>
                    <ul className="space-y-1.5">{campaign.inclusions.split(/\n|,/).filter(Boolean).map((i, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-ink-secondary"><Check className="w-4 h-4 text-moss mt-0.5 shrink-0" /> {i.trim()}</li>))}</ul></div>
                )}
                {campaign.exclusions && (
                  <div><h2 className="font-display font-extrabold text-xl mb-3">Not included</h2>
                    <ul className="space-y-1.5">{campaign.exclusions.split(/\n|,/).filter(Boolean).map((i, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-ink-muted"><X className="w-4 h-4 mt-0.5 shrink-0" /> {i.trim()}</li>))}</ul></div>
                )}
                {campaign.disclaimers && <p className="text-xs text-ink-muted border-t border-sand-dark pt-4">{campaign.disclaimers}</p>}
                <Link to="/build" data-testid="landing-build-cta" className="inline-flex items-center gap-2 border border-moss text-moss hover:bg-moss-light h-12 px-6 rounded-sm font-semibold transition-colors">Build & customise your itinerary <ArrowRight className="w-4 h-4" /></Link>
              </div>

              <div className="lg:sticky lg:top-24 h-fit">
                <div className="rounded-xl bg-white border border-sand-dark p-6 shadow-sm">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-terracotta flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Responsible Adventures</p>
                  <div className="flex rounded-sm border border-sand-dark overflow-hidden my-4">
                    <button data-testid="landing-tab-enquire" onClick={() => setMode("enquire")} className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "enquire" ? "bg-terracotta text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Enquire</button>
                    <button data-testid="landing-tab-reserve" onClick={() => setMode("reserve")} className={`flex-1 py-2 text-sm font-semibold transition-colors ${mode === "reserve" ? "bg-terracotta text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Reserve & pay</button>
                  </div>
                  {mode === "enquire" ? <LeadForm campaign={campaign} /> : <ReserveForm campaign={campaign} />}
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </RALayout>
  );
}
